import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AgenteOpcao, Equipe, Programacao, ResumoArea, ResumoProgramacao, S, liraaApi, mensagemErro, paraInt,
} from './liraaApi';

// LIRAa — Ciclo e Equipe, Aba 1 "Areas e equipe" (Sessao 30, bloco N10, 24/09/2026 - Walber).
// A tela NAO decide regra: cobertura de quarteiroes, somas e avisos vem do GET /resumo-programacao
// (motor, liraa_calculo.resumo_programacao). Aqui so a previa de programados ao digitar (metade, pra cima).

// ── Cores do desenho aprovado ────────────────────────────────────────────────
export const C = {
  fundo: '#EEF2F7', borda: '#D5DEE9', texto: '#0F1E36', sub: '#3D4F66', sub2: '#5B6B80',
  teal: '#0E7490', tealHover: '#0B5A70', tealClaro: '#E0F2F7', cabAberto: '#F3FAFC', abas: '#DCE4EE',
  ambarF: '#FEF3C7', ambarT: '#78350F', verdeF: '#DCFCE7', verdeT: '#14532D', cinzaF: '#F1F5F9',
  vermelho: '#B42318',
};
export const CARTAO: React.CSSProperties = { background: '#ffffff', border: `1px solid ${C.borda}`, borderRadius: 13, marginBottom: 12 };
export const BOTAO_TEAL: React.CSSProperties = {
  minHeight: 40, padding: '8px 16px', borderRadius: 9, border: `2px solid ${C.teal}`, background: C.teal, color: '#ffffff',
  fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
};
export const BOTAO_CLARO: React.CSSProperties = { ...BOTAO_TEAL, background: '#ffffff', color: C.texto, borderColor: '#9FB0C4' };
// hover do teal (estilo inline nao tem :hover) e empilhamento no celular
export const CSS_LIRAA = `.liraa-teal:hover:not(:disabled){background:${C.tealHover}!important;border-color:${C.tealHover}!important}
@media (max-width: 720px){.liraa-grade-sorteio{grid-template-columns:1fr!important}}`;

export const metade = (n: number) => Math.ceil(n / 2);
export const fmtMil = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('pt-BR'));
const previaMetade = (txt: string) => { const v = paraInt(txt); return v === null || Number.isNaN(v) ? '—' : fmtMil(metade(v)); };
const lista = (txt: string | null | undefined) => (txt || '').split(',').map(x => x.trim()).filter(Boolean);
const numTxt = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

// Bloco N8: caixa de quarteiroes que CRESCE com o conteudo (sem barra de rolagem).
// Enter bloqueado e quebra de linha colada vira espaco: o texto continua uma lista separada por virgula.
export function CaixaQuarteiroes({ value, onChange, placeholder, style }: {
  value: string; onChange: (v: string) => void; placeholder?: string; style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const ajustar = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
  }, []);
  useLayoutEffect(ajustar, [value, ajustar]);
  useEffect(() => {   // a largura da coluna muda (janela, tabela): recalcula a altura
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let largura = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== largura) { largura = el.clientWidth; ajustar(); }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ajustar]);
  return (
    <textarea ref={ref} rows={1} value={value} placeholder={placeholder}
      style={{ ...S.campo, display: 'block', resize: 'none', overflow: 'hidden', lineHeight: 1.4, fontFamily: 'inherit',
        whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', textAlign: 'left', minHeight: 40, ...style }}
      onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
      onChange={e => onChange(e.target.value.replace(/\r?\n/g, ' '))} />
  );
}

// ── Selo e situacao da area (lidos do resumo do motor) ──────────────────────
export function seloArea(ar?: ResumoArea): { texto: string; ok: boolean } {
  if (!ar) return { texto: 'Ainda não salva', ok: false };
  if (ar.n_sorteados === 0) return { texto: 'Falta o sorteio', ok: false };
  if (ar.imoveis === null) return { texto: 'Falta o total de imóveis', ok: false };
  if (ar.quarteiroes_sem_agente.length) return { texto: `${ar.quarteiroes_sem_agente.length} quart. sem agente`, ok: false };
  if (ar.agentes.some(g => g.imoveis === null)) return { texto: 'Falta imóveis do agente', ok: false };
  if (ar.agentes.length && ar.soma_imoveis_agentes !== ar.imoveis) return { texto: 'Soma dos agentes diferente', ok: false };
  if (ar.avisos.some(a => a.includes('entre os sorteados'))) return { texto: 'Quarteirão fora do sorteio', ok: false };
  return { texto: '✓ Tudo certo', ok: true };
}
export const sorteioOk = (ar: ResumoArea) => ar.n_sorteados > 0 && ar.imoveis !== null;
export const equipeOk = (ar: ResumoArea) => ar.n_sorteados > 0 && ar.quarteiroes_sem_agente.length === 0
  && ar.agentes.length > 0 && ar.agentes.every(g => g.imoveis !== null);

const Ficha = ({ children, fundo = C.cinzaF, cor = C.sub, italico = false }: {
  children: React.ReactNode; fundo?: string; cor?: string; italico?: boolean;
}) => (
  <span style={{ background: fundo, color: cor, borderRadius: 999, padding: '3px 10px', fontSize: 13, fontWeight: 700,
    fontStyle: italico ? 'italic' : 'normal', whiteSpace: 'nowrap' }}>{children}</span>
);

// ── B1: "Como esta a programacao do ciclo" ─────────────────────────────────
export function ComoEsta({ resumo }: { resumo: ResumoProgramacao }) {
  const areas = resumo.por_area;
  const y = areas.length;
  const caixaSit = (titulo: string, x: number, detalhe: string, tipo: 'verde' | 'ambar' | 'cinza') => {
    const est = tipo === 'verde' ? { f: C.verdeF, t: C.verdeT, i: '✓' } : tipo === 'ambar' ? { f: C.ambarF, t: C.ambarT, i: '!' } : { f: C.cinzaF, t: C.sub, i: '·' };
    return (
      <div style={{ background: est.f, color: est.t, borderRadius: 11, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span style={{ width: 26, height: 26, borderRadius: 999, background: '#ffffff', color: est.t, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{est.i}</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{titulo} — {x} de {y} {y === 1 ? 'área' : 'áreas'}</div>
          {detalhe && <div style={{ fontSize: 13, marginTop: 2 }}>{detalhe}</div>}
        </div>
      </div>
    );
  };
  const semSorteio = areas.filter(a => !sorteioOk(a)).map(a => a.area);
  const semEquipe = areas.filter(a => !equipeOk(a)).map(a => a.area);
  const comInsp = areas.filter(a => a.inspecionados !== null).length;
  return (
    <div style={{ ...CARTAO, padding: '14px 16px' }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: C.texto, marginBottom: 10 }}>Como está a programação do ciclo {resumo.ciclo.nome}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
        {caixaSit('Sorteio informado', y - semSorteio.length, semSorteio.length ? `faltam ${semSorteio.join(', ')}` : '', semSorteio.length || !y ? 'ambar' : 'verde')}
        {caixaSit('Equipe completa', y - semEquipe.length, semEquipe.length ? `faltam ${semEquipe.join(', ')}` : '', semEquipe.length || !y ? 'ambar' : 'verde')}
        {caixaSit('Inspecionados', comInsp, 'só no fim do ciclo', comInsp === 0 ? 'cinza' : comInsp < y ? 'ambar' : 'verde')}
      </div>
    </div>
  );
}

// ── B2: cartao da area ──────────────────────────────────────────────────────
interface Rasc { sorteados: string; total: string; insp: string; }
interface RascAg { quart: string; imov: string; }
const rascDe = (p?: Programacao): Rasc => ({
  sorteados: p?.quarteiroes_sorteados || '', total: numTxt(p?.imoveis_quarteiroes_sorteados), insp: numTxt(p?.imoveis_inspecionados),
});
const rascAgDe = (e: Equipe): RascAg => ({ quart: e.quarteiroes || '', imov: numTxt(e.imoveis_agente) });
// Bloco N11: linhas novas da equipe sao uma LISTA (0..N). Seletor sempre comeca vazio (nunca pre-escolhe agente).
interface LinhaNova { chave: number; agenteId: string; quart: string; imov: string; }
const preenchida = (n: LinhaNova) => !!(n.agenteId || n.quart.trim() || n.imov.trim());

export interface CartaoAreaProps {
  token: string | null; cicloId: number; areaId: number; nome: string;
  prog?: Programacao; equipe: Equipe[]; resumoArea?: ResumoArea; agentes: AgenteOpcao[];
  aberto: boolean; onAlternar: () => void;
  onSujo: (areaId: number, sujo: boolean) => void;
  onRecarregar: () => Promise<void>;
}

export function CartaoArea(p: CartaoAreaProps) {
  const ar = p.resumoArea;
  const selo = seloArea(ar);
  const nomesAg = p.equipe.map(e => e.agente_nome || '?');
  return (
    <div id={`liraa-area-${p.areaId}`} style={{ ...CARTAO, border: p.aberto ? `2px solid ${C.teal}` : CARTAO.border, overflow: 'hidden', scrollMarginTop: 12 }}>
      <button type="button" onClick={p.onAlternar} aria-expanded={p.aberto}
        style={{ width: '100%', minHeight: 52, border: 'none', background: p.aberto ? C.cabAberto : '#ffffff', cursor: 'pointer',
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 14px', textAlign: 'left', color: C.texto, fontFamily: 'inherit' }}>
        <span style={{ fontWeight: 800, fontSize: 16, minWidth: 120 }}>{p.nome}</span>
        <Ficha>{ar ? ar.n_sorteados : 0} quart.</Ficha>
        <Ficha>{fmtMil(ar?.imoveis)} imóveis</Ficha>
        <Ficha fundo={C.tealClaro} cor={C.teal}>{fmtMil(ar?.programados)} programados</Ficha>
        {nomesAg.length
          ? <span style={{ fontSize: 14, color: C.sub }}>{nomesAg.join(', ')}</span>
          : <span style={{ fontSize: 14, color: C.ambarT, fontStyle: 'italic' }}>sem agente</span>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ background: selo.ok ? C.verdeF : C.ambarF, color: selo.ok ? C.verdeT : C.ambarT, borderRadius: 999,
            padding: '4px 12px', fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap' }}>{selo.texto}</span>
          <span style={{ fontSize: 18, color: C.sub2, transform: p.aberto ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▾</span>
        </span>
      </button>
      {p.aberto && <CorpoArea {...p} />}
    </div>
  );
}

const Passo = ({ n, titulo, cinza = false, children }: { n: number; titulo: string; cinza?: boolean; children: React.ReactNode }) => (
  <div style={{ padding: '14px 16px', borderTop: `1px solid ${C.borda}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{ width: 28, height: 28, borderRadius: 999, background: cinza ? '#94A3B8' : C.texto, color: '#ffffff', fontWeight: 800,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{n}</span>
      <span style={{ fontWeight: 800, fontSize: 15, color: C.texto }}>{titulo}</span>
    </div>
    {children}
  </div>
);
const ROTULO: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 700, color: C.sub, marginBottom: 4 };
const CAIXA_TEAL: React.CSSProperties = { ...S.campo, minHeight: 40, background: C.tealClaro, color: C.teal, borderColor: '#9CCFDD', fontWeight: 800, display: 'flex', alignItems: 'center' };
const TH: React.CSSProperties = { ...S.th, whiteSpace: 'normal', lineHeight: 1.2, textAlign: 'left' };
const TD: React.CSSProperties = { ...S.td, verticalAlign: 'top', textAlign: 'left', whiteSpace: 'normal' };

function CorpoArea(p: CartaoAreaProps) {
  const ar = p.resumoArea;
  const [rasc, setRasc] = useState<Rasc>(() => rascDe(p.prog));
  const [rascAg, setRascAg] = useState<Record<number, RascAg>>(() => Object.fromEntries(p.equipe.map(e => [e.id, rascAgDe(e)])));
  const seqNova = useRef(0);
  const linhaNova = (): LinhaNova => ({ chave: ++seqNova.current, agenteId: '', quart: '', imov: '' });
  // area sem nenhum agente: o cartao ja abre com 1 linha nova em branco
  const [novos, setNovos] = useState<LinhaNova[]>(() => (p.equipe.length === 0 ? [linhaNova()] : []));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const resetar = useRef(false);   // true: ao chegar dados novos do motor, descarta o rascunho (depois de salvar tudo certo)

  // depois de recarregar: rascunho volta para o que o motor devolveu (so quando o salvar deu tudo certo)
  useEffect(() => {
    setRascAg(x => {   // agente que entrou (recem-salvo) ganha rascunho; o que saiu (tirado da area) some
      const out: Record<number, RascAg> = {};
      p.equipe.forEach(e => { out[e.id] = resetar.current || !x[e.id] ? rascAgDe(e) : x[e.id]; });
      return out;
    });
    if (resetar.current) { setRasc(rascDe(p.prog)); resetar.current = false; }
  }, [p.prog, p.equipe]);

  const orig = rascDe(p.prog);
  const progSujo = rasc.sorteados.trim() !== orig.sorteados.trim() || rasc.total.trim() !== orig.total || rasc.insp.trim() !== orig.insp;
  const agSujos = p.equipe.filter(e => {
    const r = rascAg[e.id]; const o = rascAgDe(e);
    return r && (r.quart.trim() !== o.quart.trim() || r.imov.trim() !== o.imov);
  });
  const novosSujos = novos.filter(preenchida);   // linha nova totalmente em branco NAO conta como alteracao
  const sujo = progSujo || agSujos.length > 0 || novosSujos.length > 0;
  const quartMudou = rasc.sorteados.trim() !== orig.sorteados.trim()
    || p.equipe.some(e => rascAg[e.id] && rascAg[e.id].quart.trim() !== (e.quarteiroes || '').trim()) || novos.some(n => !!n.quart.trim());

  const { onSujo, areaId } = p;
  useEffect(() => { onSujo(areaId, sujo); }, [onSujo, areaId, sujo]);

  const naArea = new Set(p.equipe.map(e => e.agente_id));
  // opcoes da linha nova: fora os ja salvos NESTA area e os escolhidos em OUTRA linha nova (outra area pode repetir)
  const disponiveis = (n: LinhaNova) => p.agentes.filter(a => !naArea.has(a.id)
    && !novos.some(o => o.chave !== n.chave && o.agenteId === String(a.id)));
  const setNova = (chave: number, k: 'agenteId' | 'quart' | 'imov', v: string) =>
    setNovos(xs => xs.map(n => (n.chave === chave ? { ...n, [k]: v } : n)));
  const descartarNova = (chave: number) => setNovos(xs => xs.filter(n => n.chave !== chave));
  const nomeAg = (id: string) => p.agentes.find(a => a.id === Number(id))?.nome || 'novo agente';
  const sorteados = lista(ar?.sorteados);
  const semAgente = new Set(ar?.quarteiroes_sem_agente || []);

  const cancelar = () => {
    setRasc(rascDe(p.prog));
    setRascAg(Object.fromEntries(p.equipe.map(e => [e.id, rascAgDe(e)])));
    setNovos(p.equipe.length === 0 ? [linhaNova()] : []); setErro('');
  };

  const salvar = async () => {
    // 1) numeros validos antes de gravar qualquer coisa
    const invalidos: string[] = [];
    const tot = paraInt(rasc.total), insp = paraInt(rasc.insp);
    if (Number.isNaN(tot)) invalidos.push('Imóveis nesses quarteirões');
    if (Number.isNaN(insp)) invalidos.push('Imóveis inspecionados');
    agSujos.forEach(e => { if (Number.isNaN(paraInt(rascAg[e.id].imov))) invalidos.push(`Imóveis de ${e.agente_nome}`); });
    novosSujos.forEach(n => { if (Number.isNaN(paraInt(n.imov))) invalidos.push(n.agenteId ? `Imóveis de ${nomeAg(n.agenteId)} (novo na área)` : 'Imóveis da linha nova sem agente'); });
    if (invalidos.length) { setErro(`Use só números inteiros (ou deixe vazio) em: ${invalidos.join(', ')}.`); return; }

    // linha nova com quarteirões/imóveis mas sem agente: NAO grava essa linha (as outras seguem) e o digitado fica
    const semAgenteNovas = novosSujos.filter(n => !n.agenteId).map(n => (n.quart.trim()
      ? `Escolha o agente da linha com os quarteirões ${n.quart.trim()}.`
      : `Escolha o agente da linha com ${n.imov.trim()} imóveis.`));
    const aGravar = novosSujos.filter(n => n.agenteId);
    if (!progSujo && !agSujos.length && !aGravar.length) { setErro(semAgenteNovas.join('\n')); return; }

    setSalvando(true); setErro('');
    const falhas: string[] = [];
    if (progSujo) {
      try {
        await liraaApi.salvarProgramacao(p.token, p.cicloId, p.areaId, {
          quarteiroes_sorteados: rasc.sorteados.trim() || null, imoveis_quarteiroes_sorteados: tot, imoveis_inspecionados: insp,
        });
      } catch (e) { falhas.push(`Sorteio da área: ${mensagemErro(e)}`); }
    }
    for (const e of agSujos) {
      try {
        await liraaApi.editarEquipe(p.token, e.id, { quarteiroes: rascAg[e.id].quart.trim() || null, imoveis_agente: paraInt(rascAg[e.id].imov) });
      } catch (err) { falhas.push(`${e.agente_nome}: ${mensagemErro(err)}`); }
    }
    const gravadas = new Set<number>();
    for (const n of aGravar) {   // um por vez (await), nunca em paralelo
      try {
        await liraaApi.criarEquipe(p.token, p.cicloId, { area_id: p.areaId, agente_id: Number(n.agenteId),
          quarteiroes: n.quart.trim() || null, imoveis_agente: paraInt(n.imov) });
        gravadas.add(n.chave);
      } catch (err) { falhas.push(`${nomeAg(n.agenteId)} (novo na área): ${mensagemErro(err)}`); }
    }
    // gravadas viram linha normal (vem na equipe recarregada); em branco somem; falhas e sem agente ficam como estao
    setNovos(xs => xs.filter(n => preenchida(n) && !gravadas.has(n.chave)));
    resetar.current = falhas.length === 0;
    try { await p.onRecarregar(); } catch { /* o erro de carga aparece no topo */ }
    const msgs: string[] = [];
    if (falhas.length) msgs.push(`Não foi possível salvar tudo em ${p.nome}:\n- ${falhas.join('\n- ')}\nO que foi digitado continua na tela.`);
    msgs.push(...semAgenteNovas);
    if (msgs.length) setErro(msgs.join('\n'));
    setSalvando(false);
  };

  const tirar = async (e: Equipe) => {
    if (!window.confirm(`Tirar ${e.agente_nome} da área ${p.nome}?`)) return;
    setErro('');
    try { await liraaApi.excluirEquipe(p.token, e.id); await p.onRecarregar(); }
    catch (err) { setErro(`${e.agente_nome}: ${mensagemErro(err)}`); }
  };

  // conferencia da soma (numeros do resumo salvo)
  const conferencia = (() => {
    if (!ar || !ar.agentes.length) return novosSujos.length ? null : { ok: false, texto: 'Nenhum agente nesta área ainda.' };
    if (ar.imoveis === null) return { ok: false, texto: 'Falta o total de imóveis da área para conferir a soma dos agentes.' };
    const sem = ar.agentes.filter(g => g.imoveis === null).map(g => g.nome);
    if (sem.length) return { ok: false, texto: `Falta o nº de imóveis de: ${sem.join(', ')}.` };
    if (ar.soma_imoveis_agentes === ar.imoveis) return { ok: true, texto: `✓ Soma dos agentes: ${fmtMil(ar.soma_imoveis_agentes)} imóveis = ${fmtMil(ar.imoveis)} da área` };
    return { ok: false, texto: ar.avisos.find(a => a.includes('soma dos imóveis')) || `Soma dos agentes: ${fmtMil(ar.soma_imoveis_agentes)} imóveis ≠ ${fmtMil(ar.imoveis)} da área.` };
  })();
  const outrosAvisos = (ar?.avisos || []).filter(a => a.includes('entre os sorteados'));

  return (
    <div>
      <Passo n={1} titulo="Sorteio da área — do LIRAa Digital">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 3fr) minmax(150px, 1fr) minmax(150px, 1fr)', gap: 12, alignItems: 'start' }} className="liraa-grade-sorteio">
          <label><span style={ROTULO}>Quarteirões sorteados</span>
            <CaixaQuarteiroes value={rasc.sorteados} placeholder="3, 11, 20, 28 ou 45.1" onChange={v => setRasc(x => ({ ...x, sorteados: v }))} /></label>
          <label><span style={ROTULO}>Imóveis nesses quarteirões</span>
            <input style={{ ...S.campo, minHeight: 40 }} inputMode="numeric" value={rasc.total} placeholder="ex.: 120" onChange={e => setRasc(x => ({ ...x, total: e.target.value }))} /></label>
          <div><span style={ROTULO}>Programados (metade)</span>
            <div style={CAIXA_TEAL} title="Metade do total de imóveis dos quarteirões sorteados, arredondando para cima">{previaMetade(rasc.total)}</div></div>
        </div>
      </Passo>

      <Passo n={2} titulo="Quem vai trabalhar">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
          {sorteados.length === 0 && <span style={{ color: C.sub2, fontSize: 14 }}>Nenhum quarteirão sorteado salvo ainda.</span>}
          {sorteados.map(q => semAgente.has(q)
            ? <Ficha key={q} fundo={C.ambarF} cor={C.ambarT}>{q}</Ficha>
            : <Ficha key={q} fundo={C.verdeF} cor={C.verdeT}>{q}</Ficha>)}
          {sorteados.length > 0 && <span style={{ fontSize: 14, fontWeight: 700, color: C.sub, marginLeft: 6 }}>
            {sorteados.length - semAgente.size} de {sorteados.length} cobertos</span>}
          {quartMudou && <span style={{ fontSize: 13, color: C.ambarT, fontStyle: 'italic' }}>salve para atualizar</span>}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse' }}>
            <colgroup><col style={{ width: '20%' }} /><col style={{ width: '42%' }} /><col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 130 }} /></colgroup>
            <thead><tr>{['Agente', 'Quarteirões dele', 'Imóveis', 'Programados', ''].map((h, i) => <th key={i} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {p.equipe.map(e => {
                const r = rascAg[e.id] || rascAgDe(e);
                const set = (k: keyof RascAg, v: string) => setRascAg(x => ({ ...x, [e.id]: { ...r, [k]: v } }));
                return (
                  <tr key={e.id}>
                    <td style={{ ...TD, fontWeight: 700, paddingTop: 14 }}>{e.agente_nome}</td>
                    <td style={TD}><CaixaQuarteiroes value={r.quart} placeholder="quarteirões" onChange={v => set('quart', v)} /></td>
                    <td style={TD}><input style={{ ...S.campo, minHeight: 40, width: 96 }} inputMode="numeric" value={r.imov} placeholder="ex.: 60" onChange={ev => set('imov', ev.target.value)} /></td>
                    <td style={TD}><div style={{ ...CAIXA_TEAL, width: 96 }} title="Metade dos imóveis dos quarteirões do agente, arredondando para cima">{previaMetade(r.imov)}</div></td>
                    <td style={TD}><button type="button" onClick={() => tirar(e)}
                      style={{ ...BOTAO_CLARO, color: C.vermelho, borderColor: C.vermelho, padding: '8px 12px' }}>Tirar da área</button></td>
                  </tr>
                );
              })}
              {novos.map((n, i) => {
                const TDN: React.CSSProperties = { ...TD, borderTop: i === 0 ? `2px dashed ${C.borda}` : TD.borderTop };
                return (
                  <tr key={`novo-${n.chave}`}>
                    <td style={TDN}>
                      <select style={{ ...S.campo, minHeight: 40 }} value={n.agenteId} autoComplete="off" aria-label="Agente da linha nova"
                        onChange={e => setNova(n.chave, 'agenteId', e.target.value)}>
                        <option value="">Escolha o agente…</option>
                        {disponiveis(n).map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                      </select></td>
                    <td style={TDN}><CaixaQuarteiroes value={n.quart} placeholder="quarteirões" onChange={v => setNova(n.chave, 'quart', v)} /></td>
                    <td style={TDN}><input style={{ ...S.campo, minHeight: 40, width: 96 }} inputMode="numeric" value={n.imov} placeholder="imóveis" onChange={e => setNova(n.chave, 'imov', e.target.value)} /></td>
                    <td style={TDN}><div style={{ ...CAIXA_TEAL, width: 96 }} title="Metade dos imóveis dos quarteirões do agente, arredondando para cima">{previaMetade(n.imov)}</div></td>
                    <td style={TDN}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: C.sub2 }}>entra ao salvar a área</span>
                        <button type="button" aria-label="Descartar esta linha" title="Descartar esta linha" onClick={() => descartarNova(n.chave)}
                          style={{ ...BOTAO_CLARO, minWidth: 40, padding: '6px 10px', justifyContent: 'center', fontSize: 18, lineHeight: 1, flex: '0 0 auto' }}>×</button>
                      </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={() => setNovos(xs => [...xs, linhaNova()])}
          style={{ ...BOTAO_CLARO, color: C.teal, borderColor: C.teal, marginTop: 10 }}>+ Adicionar agente</button>
        {conferencia && <div style={{ marginTop: 10, background: conferencia.ok ? C.verdeF : C.ambarF, color: conferencia.ok ? C.verdeT : C.ambarT,
          borderRadius: 9, padding: '8px 12px', fontSize: 14, fontWeight: 700 }}>{conferencia.texto}</div>}
        {outrosAvisos.map((a, i) => (
          <div key={i} style={{ marginTop: 6, background: C.ambarF, color: C.ambarT, borderRadius: 9, padding: '8px 12px', fontSize: 14 }}>{a}</div>
        ))}
      </Passo>

      <Passo n={3} titulo="Fim do ciclo" cinza>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <label><span style={ROTULO}>Imóveis inspecionados</span>
            <input style={{ ...S.campo, minHeight: 40, width: 150 }} inputMode="numeric" value={rasc.insp} onChange={e => setRasc(x => ({ ...x, insp: e.target.value }))} /></label>
          <span style={{ fontSize: 13, color: C.sub2, paddingBottom: 10 }}>Necessário para o IIP e o consolidado.</span>
        </div>
      </Passo>

      {erro && <div style={{ margin: '0 16px 12px', background: '#FEE4E2', color: '#7A271A', border: '1px solid #F9B8B0', borderRadius: 9, padding: '10px 12px', whiteSpace: 'pre-line', fontSize: 14 }}>{erro}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 16px', borderTop: `1px solid ${C.borda}`, background: C.cabAberto }}>
        <button type="button" style={BOTAO_CLARO} onClick={cancelar} disabled={salvando || !sujo}>Cancelar</button>
        <button type="button" className="liraa-teal" style={BOTAO_TEAL} onClick={salvar} disabled={salvando || !sujo}>
          {salvando ? 'Salvando…' : `Salvar ${p.nome}`}</button>
      </div>
    </div>
  );
}

// ── B3: area fora do LIRAa ──────────────────────────────────────────────────
export function AreaFora({ nome, onIncluir }: { nome: string; onIncluir: () => void }) {
  return (
    <div style={{ border: `2px dashed ${C.borda}`, borderRadius: 13, padding: '10px 14px', marginBottom: 10, display: 'flex',
      flexWrap: 'wrap', alignItems: 'center', gap: 10, color: C.sub2, background: 'transparent' }}>
      <span><b style={{ color: C.sub }}>{nome}</b> — Não participa deste ciclo.</span>
      <button type="button" style={{ ...BOTAO_CLARO, marginLeft: 'auto' }} onClick={onIncluir}>Incluir no ciclo</button>
    </div>
  );
}
